import { useCallback, useEffect, useState, type ReactNode } from "react";

import { ApiError, api } from "../../api/client";
import type { LeaveEntitlement, LeaveRequest, LeaveType } from "../../types";
import { formatLeaveTypeName } from "../../utils/formatters";

// TODO: wrap labels in t() once admin panel i18n pass lands.

interface Props {
  employeeId: number;
}

export default function EmployeeLeaveManagement({ employeeId }: Props) {
  const [tab, setTab] = useState<"balances" | "add">("balances");

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          Leave Management
        </h2>
        <div className="flex gap-1 text-xs">
          <TabBtn active={tab === "balances"} onClick={() => setTab("balances")}>
            Balances
          </TabBtn>
          <TabBtn active={tab === "add"} onClick={() => setTab("add")}>
            Add Leave
          </TabBtn>
        </div>
      </div>

      {tab === "balances" && (
        <BalancesTab employeeId={employeeId} />
      )}
      {tab === "add" && (
        <AddLeaveTab employeeId={employeeId} />
      )}
    </section>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-full px-3 py-1 font-semibold",
        active ? "bg-pme-blue text-white" : "bg-gray-100 text-gray-600",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

// ── Balances tab ─────────────────────────────────────────────────────────────

function BalancesTab({ employeeId }: { employeeId: number }) {
  const [rows, setRows] = useState<LeaveEntitlement[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get<LeaveEntitlement[]>(
        `/api/leave/entitlements?employee_id=${employeeId}`,
      );
      setRows(data);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [employeeId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <p className="text-sm text-red-600">{error}</p>;

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-3 py-2">Leave Type</th>
            <th className="px-3 py-2 text-right">Entitlement</th>
            <th className="px-3 py-2 text-right">Carried Over</th>
            <th className="px-3 py-2 text-right">Adjustments</th>
            <th className="px-3 py-2 text-right">Used</th>
            <th className="px-3 py-2 text-right">Remaining</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="px-3 py-2">
                <span
                  aria-hidden
                  className="mr-2 inline-block h-2 w-2 rounded-full align-middle"
                  style={{ backgroundColor: r.leave_type_colour ?? "#0872c1" }}
                />
                {formatLeaveTypeName(r.leave_type_code, r.leave_type_name)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{r.total_days}</td>
              <td className="px-3 py-2 text-right tabular-nums">{r.carried_over_days}</td>
              <td className="px-3 py-2 text-right tabular-nums">{r.adjustment_days}</td>
              <td className="px-3 py-2 text-right tabular-nums">{r.used_days}</td>
              <td className="px-3 py-2 text-right tabular-nums font-semibold">
                {Number(r.total_days) > 0 ? r.remaining_days : "—"}
              </td>
              <td className="px-3 py-2 text-right">
                <button
                  type="button"
                  onClick={() => setEditingId(r.id)}
                  className="text-xs text-pme-blue hover:underline"
                >
                  Adjust
                </button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={7} className="px-3 py-6 text-center text-gray-400">
                No entitlements found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {editingId != null && (
        <AdjustModal
          entitlement={rows.find((r) => r.id === editingId)!}
          onClose={() => setEditingId(null)}
          onSaved={() => {
            setEditingId(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function AdjustModal({
  entitlement,
  onClose,
  onSaved,
}: {
  entitlement: LeaveEntitlement;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [totalDays, setTotalDays] = useState(entitlement.total_days);
  const [carriedOver, setCarriedOver] = useState(entitlement.carried_over_days);
  const [adjustment, setAdjustment] = useState(entitlement.adjustment_days);
  const [notes, setNotes] = useState(entitlement.notes ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.put(`/api/leave/entitlements/${entitlement.id}`, {
        total_days: Number(totalDays),
        carried_over_days: Number(carriedOver),
        adjustment_days: Number(adjustment),
        notes: notes.trim() || null,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 md:items-center md:px-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        className="w-full rounded-t-xl bg-white p-5 shadow-2xl md:max-w-md md:rounded-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-gray-900">
          Adjust —{" "}
          {formatLeaveTypeName(entitlement.leave_type_code, entitlement.leave_type_name)}
        </h3>
        <p className="mt-1 text-xs text-gray-500">
          Currently used: {entitlement.used_days} days
        </p>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="block text-sm">
            <span className="text-xs font-medium text-gray-700">Entitlement</span>
            <input
              type="number"
              step="0.5"
              value={totalDays}
              onChange={(e) => setTotalDays(e.target.value)}
              className="input mt-1"
            />
          </label>
          <label className="block text-sm">
            <span className="text-xs font-medium text-gray-700">Carried over</span>
            <input
              type="number"
              step="0.5"
              value={carriedOver}
              onChange={(e) => setCarriedOver(e.target.value)}
              className="input mt-1"
            />
          </label>
          <label className="block text-sm">
            <span className="text-xs font-medium text-gray-700">Adjustment</span>
            <input
              type="number"
              step="0.5"
              value={adjustment}
              onChange={(e) => setAdjustment(e.target.value)}
              className="input mt-1"
            />
          </label>
        </div>

        <label className="mt-3 block text-sm">
          <span className="text-xs font-medium text-gray-700">
            Reason for adjustment
          </span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="e.g. Migrated from previous system, correction for 2025 carry-over"
            className="input mt-1"
          />
        </label>

        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-lg bg-pme-blue px-5 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Add Leave tab ────────────────────────────────────────────────────────────

function AddLeaveTab({ employeeId }: { employeeId: number }) {
  const [types, setTypes] = useState<LeaveType[]>([]);
  const [typeId, setTypeId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [halfStart, setHalfStart] = useState(false);
  const [halfEnd, setHalfEnd] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const t = await api.get<LeaveType[]>("/api/leave/types");
        setTypes(t);
        if (t.length && typeId === null) setTypeId(t[0].id);
      } catch {
        /* ignore */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!typeId || !startDate || !endDate) return;
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await api.post<LeaveRequest>(
        "/api/leave/requests/on-behalf",
        {
          employee_id: employeeId,
          leave_type_id: typeId,
          start_date: startDate,
          end_date: endDate,
          is_half_day_start: halfStart,
          is_half_day_end: halfEnd,
          reason: reason.trim() || null,
        },
      );
      setSuccess(
        `Recorded ${result.total_days} days of ${formatLeaveTypeName(result.leave_type_code, result.leave_type_name)} ` +
          `(${result.start_date} → ${result.end_date}). Balance updated.`,
      );
      setStartDate("");
      setEndDate("");
      setHalfStart(false);
      setHalfEnd(false);
      setReason("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to create leave record.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="mb-3 text-xs text-gray-500">
        Record historical leave or enter leave on behalf of an employee. The
        request is auto-approved and the balance deducted immediately.
      </p>

      <form onSubmit={submit} className="space-y-3">
        <label className="block text-sm">
          <span className="text-xs font-medium text-gray-700">Leave type</span>
          <select
            value={typeId ?? ""}
            onChange={(e) => setTypeId(Number(e.target.value))}
            className="input mt-1"
          >
            {types.map((ty) => (
              <option key={ty.id} value={ty.id}>
                {formatLeaveTypeName(ty.code, ty.name)}
              </option>
            ))}
          </select>
        </label>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="text-xs font-medium text-gray-700">Start date</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                if (!endDate || endDate < e.target.value) setEndDate(e.target.value);
              }}
              required
              className="input mt-1"
            />
          </label>
          <label className="block text-sm">
            <span className="text-xs font-medium text-gray-700">End date</span>
            <input
              type="date"
              value={endDate}
              min={startDate}
              onChange={(e) => setEndDate(e.target.value)}
              required
              className="input mt-1"
            />
          </label>
        </div>

        <div className="flex flex-wrap gap-3 text-xs">
          <label className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={halfStart}
              onChange={(e) => setHalfStart(e.target.checked)}
            />
            Half day start (morning only)
          </label>
          {startDate !== endDate && (
            <label className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={halfEnd}
                onChange={(e) => setHalfEnd(e.target.checked)}
              />
              Half day end (afternoon only)
            </label>
          )}
        </div>

        <label className="block text-sm">
          <span className="text-xs font-medium text-gray-700">
            Reason / notes
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="e.g. Historical — migrated from spreadsheet"
            className="input mt-1"
          />
        </label>

        {error && <p className="text-sm text-red-600">{error}</p>}
        {success && <p className="text-sm text-green-600">{success}</p>}

        <button
          type="submit"
          disabled={submitting || !typeId || !startDate || !endDate}
          className="w-full rounded-lg bg-pme-blue py-3 text-sm font-semibold text-white disabled:opacity-60 sm:w-auto sm:px-5"
        >
          {submitting ? "Recording..." : "Record Leave"}
        </button>
      </form>
    </div>
  );
}
